import { useEffect, useLayoutEffect, useRef, useState, type FC, type SVGProps } from 'react';
import type { TenantSession } from '../OrganizationsSlice';
import { getIndicatorType, type IndicatorType } from '../OrganizationsSlice/indicators';
import { NotificationBubble } from '../../../components/NotificationBubble/NotificationBubble';
import { ReactComponent as AlarmIcon } from '../../../assets/icons/bell-white.svg';
import { ReactComponent as ImOkIcon } from '../../../assets/icons/thumbs-up-white.svg';
import { ReactComponent as MessageIcon } from '../../../assets/icons/envelope-white.svg';
import { SLogoFallback, SLogoOverlay, SLogoWithBadge } from './SessionIndicator.styles';
import {
  hasBubbleId,
  addBubbleId,
  deleteBubbleIdsForIdentity,
} from '../../../utils/bubbleEntranceStorage';

const INDICATOR_ICON: Record<IndicatorType, FC<SVGProps<SVGSVGElement>>> = {
  alarm: AlarmIcon,
  imOk: ImOkIcon,
  message: MessageIcon,
};

const getInitial = (name: string | null | undefined): string => {
  if (!name) return '?';
  return name.trim().charAt(0).toUpperCase() || '?';
};

type Props = {
  session: TenantSession;
  size: number;
  /** Suppress the indicator bubble (e.g. for the active session). */
  showIndicator?: boolean;
  /** Apply a 2px punched-out outline (matches the bubble border) — used in stacked groups. */
  bordered?: boolean;
  /** Namespace for entrance-animation tracking — pill and menu track independently. */
  bubbleContext?: string;
};

/**
 * Tenant avatar with an optional indicator bubble overlaid at top-right. The
 * fallback initials act as a skeleton — they're shown immediately and the real
 * logo fades in on top of them once it has loaded. The indicator bubble waits
 * for the logo so it doesn't pop in before its avatar.
 */
export const SessionIndicator = ({
  session,
  size,
  showIndicator = true,
  bordered = false,
  bubbleContext,
}: Props) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const handleSettled = () => setImgLoaded(true);

  // The bubble is gated on the avatar being visible: if there's no logo URL
  // the fallback is the whole avatar; if there is one, we wait for it to load.
  const avatarReady = !session.tenantLogo || imgLoaded;
  // rawIndicatorType reflects the session's actual indicator state regardless of showIndicator.
  // type is what we actually render — suppressed for the active session / stacked items.
  const rawIndicatorType = getIndicatorType(session);
  const type = showIndicator ? rawIndicatorType : null;
  const Icon = type ? INDICATOR_ICON[type] : null;

  // Use tenantId as the stable, unique base key to avoid collisions on name changes/empty names.
  const baseIdentity = String(session.tenantId);
  const bubbleIdentity = bubbleContext ? `${baseIdentity}:${bubbleContext}` : baseIdentity;
  const [skipEntrance] = useState(() => hasBubbleId(bubbleIdentity));

  // hasWrittenRef prevents writing the "seen" flag more than once per mount lifecycle,
  // even if type transitions null → non-null without a remount (e.g., session becomes inactive).
  const hasWrittenRef = useRef(skipEntrance);

  useLayoutEffect(() => {
    // Write as soon as the indicator is first visible so remounts skip the entrance.
    if (hasWrittenRef.current || type === null) return;
    addBubbleId(bubbleIdentity);
    hasWrittenRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    // Base the clear on rawIndicatorType so it fires when the indicator truly disappears,
    // not when showIndicator is suppressed (e.g. for the active session avatar).
    if (rawIndicatorType === null) {
      // Delete all context variants (pill, menu, etc.) so re-appearance animates everywhere.
      deleteBubbleIdsForIdentity(baseIdentity);
    }
  }, [rawIndicatorType, baseIdentity]);

  return (
    <SLogoWithBadge>
      <SLogoFallback $size={size} $bordered={bordered}>
        {getInitial(session.tenantName)}
      </SLogoFallback>
      {session.tenantLogo && (
        <SLogoOverlay
          src={session.tenantLogo}
          alt=""
          $size={size}
          $bordered={bordered}
          $loaded={imgLoaded}
          onLoad={handleSettled}
          onError={handleSettled}
        />
      )}
      {Icon && (skipEntrance || avatarReady) && (
        <NotificationBubble icon={<Icon />} isDanger skipEntrance={skipEntrance} />
      )}
    </SLogoWithBadge>
  );
};
